/**
 * Auth state. One gate: signed in, or not.
 *
 * There used to be a second — the vault unlock, which derived an encryption key
 * from a passphrase shared with leeseidon. It is gone along with the shared
 * backend. Nothing stands between signing in and using the app.
 *
 * The session is obtained from a Google credential **without ever navigating**
 * (INV-8), which remains load-bearing: on iOS an installed PWA and Safari have
 * separate storage jars, and a redirect flow can write the session into the one
 * the app cannot read.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  type Session,
  clearSession,
  readSession,
  writeSessionDurably,
} from "@/lib/backend";
import { setCurrentUser } from "@/lib/repo";
import {
  type AdoptOutcome,
  type LeaveOutcome,
  adoptReauthenticatedSession,
  endSession,
  prepareToLeave,
  resolveDeviceOwner,
} from "@/lib/session";
import { reportSessionExpired, subscribeSync } from "@/lib/sync";

/**
 * `"session-expired"` is deliberately **not** `"signed-out"`.
 *
 * Signed-out means "show the sign-in screen and nothing else". Expired means the
 * token was rejected but the ledger on this device is intact and belongs to a
 * known account — so the app keeps working exactly as it does offline, and the
 * user is offered a way back rather than a wall.
 *
 * Collapsing the two would put `<SignInView />` in front of the numpad the
 * moment a session died, which would make logging a coffee depend on being
 * online. That is the one thing this product cannot do (INV-4, SPEC §3).
 */
export type AuthState = "loading" | "signed-out" | "signed-in" | "session-expired";

/**
 * What a sign-out attempt came to. `blocked` means **nothing happened**: rows
 * are pending that could not upload, and the caller shows the leave sheet
 * (`features/onboarding/leave_sheets.tsx`).
 */
export type SignOutOutcome = { kind: "signed-out" } | Extract<LeaveOutcome, { kind: "blocked" }>;

interface AuthValue {
  state: AuthState;
  session: Session | null;
  userId: string | null;
  /**
   * Adopt a freshly-issued session — the **only** way in. Keeps the local ledger
   * when it belongs to the same account, clears it silently when another
   * account's ledger has nothing pending, and resolves `blocked` — touching
   * nothing — when it does. `discard: true` is the user's answer to that sheet.
   *
   * There is deliberately no unguarded `applySession` on this context. The
   * device can now hold a ledger while showing a sign-in surface — that is what
   * `"session-expired"` is — so an unchecked adopt would let one account inherit
   * another's rows, and the reads in `use_data.ts` are not user-scoped. Making
   * the checked path the only path is a structural guarantee rather than a rule
   * someone has to remember.
   */
  applySession: (session: Session, options?: { discard?: boolean }) => Promise<AdoptOutcome>;
  /**
   * Upload, then clear. Resolves `blocked` without touching anything when rows
   * are pending that cannot upload; `discard: true` skips the upload and is the
   * user's explicit "Sign out and discard N changes".
   */
  signOut: (options?: { discard?: boolean }) => Promise<SignOutOutcome>;
  /** Whether the "Sign in again" sheet is open. One sheet, opened from anywhere. */
  reauthOpen: boolean;
  /** Open the "Sign in again" sheet — from the band, Horizon, Settings or a leave sheet. */
  requestReauth: () => void;
  closeReauth: () => void;
  /**
   * An Apple sign-in just created a new account. Shown once: someone who used
   * Google before has just got a second, empty account, and the remedy —
   * sign out, continue with Google, link Apple in Settings › Account — is not
   * something they would guess (SPEC §11.1). Emails are never matched.
   */
  newAccountNotice: boolean;
  setNewAccountNotice: (show: boolean) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [state, setState] = useState<AuthState>("loading");

  useEffect(() => {
    const existing = readSession();
    if (existing) {
      setSession(existing);
      setCurrentUser(existing.accountId);
      setState("signed-in");
      return;
    }

    // No token — but "no token" is not the same as "signed out".
    //
    // An expired session clears the token, and an installed PWA is killed and
    // cold-started constantly. Treating every tokenless boot as signed-out would
    // therefore put `SignInView` in front of the numpad on the very next launch,
    // with the user's queued rows stranded behind it — which is exactly the
    // regression `"session-expired"` exists to prevent, merely deferred by one
    // app restart. If this device still holds a ledger, it still has an owner.
    void resolveDeviceOwner()
      .then((owner) => {
        if (owner === null) {
          setCurrentUser(null);
          setState("signed-out");
          return;
        }
        // Restored so `requireUser()` resolves and capture keeps working.
        setCurrentUser(owner);
        setState("session-expired");

        // Nothing else will say so on this launch: no token means `sync()`
        // returns at its own guard, so without this the app is silent about a
        // dead session whenever the outbox is empty.
        void reportSessionExpired();
      })
      .catch((cause: unknown) => {
        // Reported, not swallowed (INV-7). Signed-out is the safe direction: it
        // is the one state that asks the user to re-establish who they are,
        // rather than acting on an owner we could not read.
        console.warn(
          "Could not read which account this device's ledger belongs to:",
          cause instanceof Error ? cause.message : cause,
        );
        setCurrentUser(null);
        setState("signed-out");
      });
  }, []);

  // The server rejecting our token is the *only* honest signal that a session is
  // dead, and it already arrives: `startSyncTriggers` syncs at boot, on `online`
  // and on every `visibilitychange`, and a 401 there becomes `SessionExpiredError`.
  // Nothing observed that, which is why a revoked session went on reading as
  // signed in (§S.1).
  // Read through a ref rather than a dependency: `subscribeSync` replays the
  // current status to every new subscriber, so re-subscribing on each state
  // change would re-fire this the moment it had already handled it.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(
    () =>
      subscribeSync((status) => {
        if (status.state !== "signed-out" || stateRef.current !== "signed-in") return;

        // The token is dead; keep nothing. `sync()` returns early without a
        // session, so this also stops a pointless 401 on every app open.
        clearSession();
        setSession(null);

        // `setCurrentUser` is deliberately NOT cleared. `requireUser()` throws
        // without it and every write in the app would fail — which would defeat
        // the entire point of staying usable.
        setState("session-expired");
      }),
    [],
  );

  const [reauthOpen, setReauthOpen] = useState(false);
  const [newAccountNotice, setNewAccountNotice] = useState(false);
  const requestReauth = useCallback(() => {
    setReauthOpen(true);
  }, []);
  const closeReauth = useCallback(() => {
    setReauthOpen(false);
  }, []);

  const applySession = useCallback(async (next: Session, options: { discard?: boolean } = {}) => {
    const outcome = await adoptReauthenticatedSession(next, options);
    // `blocked` means the device was not touched, so no state may move here
    // either — the caller shows the switch sheet.
    if (outcome.kind === "blocked") return outcome;

    await writeSessionDurably(next);
    setSession(next);
    setCurrentUser(next.accountId);
    setState("signed-in");
    return outcome;
  }, []);

  const signOut = useCallback(async (options: { discard?: boolean } = {}) => {
    if (options.discard !== true) {
      const ready = await prepareToLeave();
      // Nothing was touched: the session, the ledger and the state all stand.
      if (ready.kind === "blocked") return ready;
    }
    await endSession();
    setCurrentUser(null);
    setSession(null);
    setReauthOpen(false);
    setState("signed-out");
    return { kind: "signed-out" } as const;
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      state,
      session,
      userId: session?.accountId ?? null,
      applySession,
      signOut,
      reauthOpen,
      requestReauth,
      closeReauth,
      newAccountNotice,
      setNewAccountNotice,
    }),
    [state, session, applySession, signOut, reauthOpen, requestReauth, closeReauth, newAccountNotice],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}
